import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ChevronUp, ChevronDown, DollarSign, Info, Landmark } from 'lucide-react';
import CalcSectionHeader from './CalcSectionHeader';
import AIButton from './AIButton';

const CapitalCosts = ({
 showCapEx,
 setShowCapEx,
 formData,
 setFormData,
 aiEstimatedFields,
 openAIDialog
}) => {
 return (
 <Card className="border border-border rounded-2xl overflow-hidden">
 <CalcSectionHeader
 icon={Landmark}
 title="Capital Costs (CapEx)"
 description="One-time startup investments and financing"
 expanded={showCapEx}
 onToggle={() => setShowCapEx(!showCapEx)}
 testId="section-header-capex"
 />
 {showCapEx && (
 <CardContent className="space-y-6 pt-5">
 {/* Equipment & Assets */}
 <div className="bg-gradient-to-r to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800 space-y-3">
 <p className="text-sm font-bold text-indigo-900 dark:text-indigo-100">🏭 Equipment & Assets</p>
 
 <div className="space-y-3">
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="machine_price" label="Freeze Dryer Purchase Price ($)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Full cost of freeze dryer unit including shipping
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="workspace_upgrades" label="Workspace Upgrades ($)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Electrical upgrades, shelving, prep tables, ventilation, flooring
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="initial_inventory" label="Initial Inventory/Materials ($)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 First orders of packaging, raw materials, consumables to start production
 </p>
 </div>
 </div>
 </div>

 {/* Legal & Administrative */}
 <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800 space-y-3">
 <p className="text-sm font-bold text-amber-900 dark:text-amber-100">📋 Legal & Administrative</p>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <AIButton formData={formData} setFormData={setFormData} aiEstimatedFields={aiEstimatedFields} openAIDialog={openAIDialog} fieldName="licensing_fees" label="Licensing & Permits ($)" />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Business license, food handler permits, cottage food license, health dept fees
 </p>
 </div>
 </div>

 {/* Financing Options */}
 <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800 space-y-4">
 <p className="text-sm font-bold text-green-900 dark:text-green-100">💳 Financing Options (Optional)</p>
 <p className="text-xs text-muted-foreground">
 If you're financing your freeze dryer or startup costs, enter loan details to see monthly payments
 </p>
 
 <div className="space-y-3">
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <Label className="text-sm font-medium">Loan Amount ($)</Label>
 <Input
 type="number"
 value={formData.loan_amount}
 onChange={(e) => setFormData(prev => ({ ...prev, loan_amount: e.target.value }))}
 placeholder="0"
 />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Principal amount borrowed (e.g., $5,000 for freeze dryer purchase)
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <Label className="text-sm font-medium">Interest Rate (%)</Label>
 <Input
 type="number"
 step="0.1"
 value={formData.interest_rate}
 onChange={(e) => setFormData(prev => ({ ...prev, interest_rate: e.target.value }))}
 placeholder="6.5"
 />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Annual percentage rate (APR) from your lender
 </p>
 </div>
 
 <div className="bg-white dark:bg-slate-800 p-3 rounded-lg space-y-2">
 <Label className="text-sm font-medium">Loan Term (months)</Label>
 <Input
 type="number"
 value={formData.loan_term_months}
 onChange={(e) => setFormData(prev => ({ ...prev, loan_term_months: e.target.value }))}
 placeholder="36"
 />
 <p className="text-xs text-muted-foreground flex items-start gap-1">
 <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
 Repayment period (e.g., 36 months = 3 years)
 </p>
 </div>
 </div>
 </div>

 {/* Depreciation */}
 <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border-2 border-slate-300 dark:border-slate-700">
 <div className="space-y-2">
 <Label className="font-semibold flex items-center gap-2">
 Machine Lifespan (years) *
 <Info className="w-4 h-4 text-blue-500" />
 </Label>
 <Input
 type="number"
 value={formData.machine_lifespan_years}
 onChange={(e) => setFormData(prev => ({ ...prev, machine_lifespan_years: e.target.value }))}
 placeholder="10"
 />
 <p className="text-xs text-muted-foreground">
 💡 Expected useful life for depreciation calculations (typically 7-10 years for freeze dryers)
 </p>
 </div>
 </div>
 </CardContent>
 )}
 </Card>
 );
};

export default CapitalCosts;
